export type PortalKind = 'marketing' | 'tech' | 'memberships' | 'unknown';

export type CompanyId = 'hales-ac' | 'climate' | 'abraham-ac';

export type PortalContext = {
  hostname: string;
  portal: PortalKind;
  companyId: CompanyId | null;
};

const PORTAL_MODE_STORAGE_KEY = 'rahsmatch_portal_mode';
const PORTAL_COMPANY_STORAGE_KEY = 'rahsmatch_portal_company_id';

export function isLocalHostname(hostname: string): boolean {
  const h = String(hostname || '').trim().toLowerCase();
  return h === 'localhost' || h === '127.0.0.1';
}

export function isMarketingHostname(hostname: string): boolean {
  const h = String(hostname || '').trim().toLowerCase();
  return h === 'marketing.rightawayhome.com' || isLocalHostname(h);
}

export function normalizeCompanyId(companyIdRaw: string | null | undefined): CompanyId | null {
  const raw = String(companyIdRaw || '').trim().toLowerCase();
  if (!raw) return null;

  // climate aliases
  if (
    raw === 'climate' ||
    raw === 'myccs' ||
    raw === 'my-ccs' ||
    raw.includes('my-ccs') ||
    raw.includes('myccs') ||
    raw.includes('climate') ||
    raw.includes('ccs')
  ) {
    return 'climate';
  }

  // hales aliases
  if (raw === 'hales-ac' || raw === 'halesac' || raw === 'hales' || raw.includes('hales')) {
    return 'hales-ac';
  }

  // abraham aliases
  if (raw === 'abraham-ac' || raw === 'abrahamac' || raw === 'abraham' || raw.includes('abraham')) {
    return 'abraham-ac';
  }

  return null;
}

export function getPortalContextFromLocation(location: { hostname: string; search?: string }): PortalContext {
  const hostname = String(location.hostname || '').trim().toLowerCase();
  const params = new URLSearchParams(location.search || '');

  const forcedPortal = String(params.get('portal') || '').trim().toLowerCase();

  // Persist local dev overrides so redirects (Navigate) don't drop the portal mode.
  if (typeof window !== 'undefined' && isLocalHostname(hostname)) {
    if (forcedPortal === 'tech' || forcedPortal === 'marketing') {
      try {
        window.sessionStorage.setItem(PORTAL_MODE_STORAGE_KEY, forcedPortal);

        const forcedCompany =
          normalizeCompanyId(params.get('company_id') || params.get('company') || params.get('companyId') || undefined) ||
          normalizeCompanyId(hostname) ||
          '';

        if (forcedCompany) {
          window.sessionStorage.setItem(PORTAL_COMPANY_STORAGE_KEY, forcedCompany);
        }
      } catch {
        // ignore
      }
    } else {
      try {
        const storedPortal = window.sessionStorage.getItem(PORTAL_MODE_STORAGE_KEY);
        if (storedPortal) {
          const storedCompany = window.sessionStorage.getItem(PORTAL_COMPANY_STORAGE_KEY);
          const company = normalizeCompanyId(storedCompany) || normalizeCompanyId(hostname) || 'climate';
          if (storedPortal === 'tech') return { hostname, portal: 'tech', companyId: company };
          if (storedPortal === 'marketing') return { hostname, portal: 'marketing', companyId: null };
        }
      } catch {
        // ignore
      }
    }
  }

  if (forcedPortal === 'tech') {
    const forcedCompany =
      normalizeCompanyId(params.get('company_id') || params.get('company') || params.get('companyId') || undefined) ||
      normalizeCompanyId(hostname) ||
      'climate';

    return { hostname, portal: 'tech', companyId: forcedCompany };
  }

  if (forcedPortal === 'marketing') {
    return { hostname, portal: 'marketing', companyId: null };
  }

  if (hostname.startsWith('tech.')) {
    return { hostname, portal: 'tech', companyId: normalizeCompanyId(hostname) };
  }

  if (hostname.startsWith('memberships.')) {
    return { hostname, portal: 'memberships', companyId: normalizeCompanyId(hostname) };
  }

  if (isMarketingHostname(hostname)) {
    return { hostname, portal: 'marketing', companyId: null };
  }

  return { hostname, portal: 'unknown', companyId: null };
}

export function getPortalContext(): PortalContext {
  if (typeof window === 'undefined') {
    return { hostname: '', portal: 'marketing', companyId: null };
  }

  return getPortalContextFromLocation(window.location);
}
